/**
 * A piece of equipment, on the shared record page.
 *
 * What it replaces is `equipment-detail.tsx`, which drew its own header, its
 * own four-tab row and its own two-column grid — and so had no Activity, no
 * Files, no History and no phone layout. A truck is the record in Rowted that
 * most wants a photo: the damage somebody rang about, the plate renewal, the
 * receipt for the tyres. It could not have one.
 *
 * `docs/record-page-contract.md` is the rulebook. Everything visible here
 * except the mark, the rail and the three own tabs belongs to `<RecordPage>`
 * and is identical on a contact and a job.
 *
 * **No related-list tab.** A piece of equipment's link to work is a booking on
 * `visit_equipment_assignments`, not a column on a visit, so "the visits
 * this was on" is not a filter the visits area can express — and a tab that
 * faked it with one assignment per visit would be wrong the first time a
 * crew took a truck and its trailer. Where its hours went is the Costs tab,
 * from `equipment_usage`, which is the honest version of the same question.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, type NavigateFunction } from 'react-router-dom'
import type { MeResponse } from '@rowted/shared'
import { equipmentMark } from '../components/landing-marks.js'
import { RecordPage, type RecordConfig } from '../record/record-page.js'
import {
  renderStandardTab, standardTabs,
} from '../record/record-standard.js'
import { AboutCard, RecordCard, type AboutField } from '../record/record-rail.js'
import { completeActivity, useRecordSummary } from '../lib/use-record.js'
import {
  Button, Card, EmptyState, KVGrid, ProgressBar, SectionLabel, Tag,
} from '../components/primitives.js'
import { Modal, SlideOver, Toast } from '../components/overlays.js'
import { Field } from '../components/fields.js'
import { EquipmentForm } from '../components/forms/equipment-form.js'
import { NAV_ICONS, IconEdit, IconNote, IconPin, IconUpload } from '../frame/icons.js'
import {
  EM_DASH, formatDateOnly, formatDocumentDate, formatNumber, formatRate, formatRelative,
} from '../lib/format.js'
import { request } from '../lib/api.js'
import { cn } from '../lib/cn.js'
import { EQUIPMENT_STATUS } from '../lib/equipment-types.js'
import type { EquipmentDetail as Detail, ServiceLine, WorkOrder } from '../lib/equipment-types.js'

/** The shape the config closes over — the payload, plus what only this page knows. */
interface EquipmentRecordData extends Detail {
  canEdit: boolean
  orgUnitId: string
  onEdit: () => void
  onRaise: () => void
  onReload: () => void
  onUpload: () => void
}


export function EquipmentRecord({ me }: { me: MeResponse }) {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const [data, setData] = useState<Detail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [people, setPeople] = useState<{ id: string; name: string }[]>([])
  const [raising, setRaising] = useState(false)
  const [editing, setEditing] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [completing, setCompleting] = useState<string | null>(null)

  const openUpload = useRef<(() => void) | null>(null)

  const { summary, reload: reloadSummary } = useRecordSummary('equipment', id || undefined)

  const canEdit = me.permissions.equipment === 'edit' || me.permissions.equipment === 'full'

  const load = useCallback(async () => {
    try {
      setData(await request<Detail>(`/equipment/${id}`))
      setError(null)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That could not be loaded.')
    }
  }, [id])

  useEffect(() => { void load() }, [load])

  useEffect(() => {
    void request<{ items: { id: string; name: string }[] }>('/people?limit=200')
      .then((body) => setPeople(body.items))
      .catch(() => setPeople([]))
  }, [])

  const complete = useCallback(async (activityId: string) => {
    setCompleting(activityId)
    try {
      /* One occurrence of a repeating meeting by itself (`completeKey`, 0152). */
      await completeActivity(activityId)
      await reloadSummary()
    } finally {
      setCompleting(null)
    }
  }, [reloadSummary])

  const record = useMemo<EquipmentRecordData | null>(() => (data ? {
    ...data,
    canEdit,
    orgUnitId: data.equipment.orgUnitId,
    onEdit: () => setEditing(true),
    onRaise: () => setRaising(true),
    onReload: () => { void load(); void reloadSummary() },
    onUpload: () => openUpload.current?.(),
  } : null), [data, canEdit, load, reloadSummary])

  const config = useMemo(() => equipmentConfig(navigate), [navigate])

  if (error) {
    return (
      <EmptyState
        title="Not found"
        body={error}
        action={{ label: 'Back to the yard', onClick: () => navigate('/equipment') }}
      />
    )
  }
  if (!record) return <p className="p-4 text-cell text-text-muted">Loading…</p>

  const piece = record.equipment

  return (
    <>
      <RecordPage config={config} record={record} summary={summary}>
        {(tab) => renderStandardTab(tab, {
          object: 'equipment',
          id: piece.id,
          href: `/equipment/${piece.id}`,
          summary,
          people,
          orgUnitId: piece.orgUnitId,
          /*
           * Work orders are the piece's own record of what has gone wrong with
           * it, so History is told about them: "who raised it and who closed
           * it" is the question somebody asks of a truck, and it is not on
           * `equipment` itself.
           */
          history: {
            table: 'equipment',
            related: {
              tables: ['work_orders'],
              recordIds: record.workOrders.map((order) => order.id),
            },
          },
          busyId: completing,
          canEdit,
          onComplete: (activityId) => void complete(activityId),
          onChanged: () => void reloadSummary(),
          onUploadReady: (open) => { openUpload.current = open },
          onOpen: (path) => navigate(path),
        }) ?? (
          tab === 'service'
            ? (
              <ServiceTab
                record={record}
                onChanged={(message) => { setNotice(message); record.onReload() }}
              />
            )
            : tab === 'checks'
              ? <Checks checks={record.quickChecks} />
              : tab === 'costs'
                ? <Costs detail={record} />
                : null
        )}
      </RecordPage>

      <EquipmentForm
        open={editing}
        onClose={() => setEditing(false)}
        orgUnitId={piece.orgUnitId}
        existing={record}
        onSaved={() => { setEditing(false); setNotice('Saved.'); record.onReload() }}
      />

      {raising && (
        <RaiseWorkOrder
          equipmentId={piece.id}
          equipmentName={piece.name}
          mechanic={record.mechanic}
          onClose={() => setRaising(false)}
          onRaised={(message) => { setRaising(false); setNotice(message); record.onReload() }}
        />
      )}

      {notice && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2">
          <Toast onDismiss={() => setNotice(null)}>{notice}</Toast>
        </div>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// The configuration
// ---------------------------------------------------------------------------

function equipmentConfig(navigate: NavigateFunction): RecordConfig<EquipmentRecordData> {
  return {
    object: 'equipment',

    breadcrumb: (record) => [
      { label: 'Equipment', href: '/equipment' },
      { label: record.equipment.assetTag },
    ],

    /*
     * The square is the asset tag over a truck, coloured by status — the one
     * thing that is written on the side of the machine and the one thing that
     * decides whether it can go out today.
     */
    /*
     * The type's glyph in the type's colour with a status dot, at 56 — the
     * same tile the Equipment list draws. The asset tag it used to carry is
     * the primary column's second line and the breadcrumb, so the mark can go
     * back to saying what kind of machine this is.
     */
    mark: (record) => equipmentMark({
      id: record.equipment.id,
      status: record.equipment.status,
      typeName: record.equipment.typeName,
      typeIcon: record.equipment.typeIcon,
      typeColour: record.equipment.typeColour,
    }, 56),

    title: (record) => record.equipment.name,

    /*
     * The status, and — separately — whether anything open is keeping it in.
     * They are different facts: a piece can be Available with three niggles
     * on it, and a piece can be In shop for something nobody has raised.
     */
    tags: (record) => {
      const status = record.equipment.status
      const grounded = record.workOrders.some(
        (order) => order.closedAt === null && order.takesOutOfService)
      return [
        {
          label: EQUIPMENT_STATUS[status]?.label ?? status,
          /* The equipment palette, which has had a pair for each of these
             states all along — the header used to borrow the *job* tokens
             through a three-bucket tone, so In shop wore Cancelled's grey. */
          tone: {
            fg: `rgb(var(--status-equipment-${status}-fg))`,
            bg: `rgb(var(--status-equipment-${status}-bg))`,
          },
        },
        ...(grounded
          ? [{
            label: 'Off the road',
            tone: {
              fg: 'rgb(var(--status-job-returned-fg))',
              bg: 'rgb(var(--status-job-returned-bg))',
            },
          }]
          : []),
      ]
    },

    meta: (record) => {
      const piece = record.equipment
      const overdue = record.service.filter((line) => line.overdue).length
      return [
        ...(overdue > 0
          ? [{
            icon: <NAV_ICONS.equipmentService size={13} />,
            text: `${overdue} service ${overdue === 1 ? 'item' : 'items'} overdue`,
          }]
          : []),
        ...([piece.year, piece.make, piece.model].filter(Boolean).length > 0
          ? [{ text: [piece.year, piece.make, piece.model].filter(Boolean).join(' ') }]
          : []),
        ...(piece.typeName ? [{ text: piece.typeName }] : []),
        ...(piece.orgUnitName
          ? [{ icon: <IconPin size={13} />, text: piece.orgUnitName }]
          : []),
        ...(record.livesWith ? [{ text: `Always with ${record.livesWith.name}` }] : []),
      ]
    },

    /*
     * Raising a work order is the one thing this page exists to do that
     * changes anything. Editing the piece is the record's own form, which is
     * why it sits beside rather than in front.
     */
    primary: (record) => (record.canEdit
      ? { label: 'New work order', onClick: record.onRaise }
      : null),

    actions: (record) => (record.canEdit
      ? [{ label: 'Edit', icon: <IconEdit size={14} />, onClick: record.onEdit }]
      : []),

    tabs: (record, summary) => standardTabs({
      counts: summary?.counts ?? {},
      own: [
        {
          key: 'service',
          label: 'Service',
          icon: 'equipmentService' as const,
          count: record.workOrders.filter((order) => order.closedAt === null).length || undefined,
        },
        { key: 'checks', label: 'Quick checks', icon: 'boolean' as const },
        { key: 'costs', label: 'Costs', icon: 'money' as const },
      ],
    }),

    rail: (record) => <EquipmentRail record={record} />,

    phone: {
      /*
       * Nothing. A truck has no address to navigate to and no number to ring
       * — where it is today is a visit, and who fixes it is the mechanic on
       * the About card.
       */
      quickActions: () => [],
      tabBar: ['overview', 'activity', 'service', 'files'],
      primaryByTab: (tab, record) => {
        if (!record.canEdit || tab === 'activity') return null
        if (tab === 'files') {
          return { label: 'Upload', icon: <IconUpload size={18} />, onClick: record.onUpload }
        }
        if (tab === 'overview') {
          return {
            label: 'Log activity',
            icon: <IconNote size={18} />,
            onClick: () => navigate(`/equipment/${record.equipment.id}?tab=activity`),
          }
        }
        return {
          label: 'New work order',
          icon: <NAV_ICONS.equipmentService size={18} />,
          onClick: record.onRaise,
        }
      },
    },
  }
}

// ---------------------------------------------------------------------------
// The rail
// ---------------------------------------------------------------------------

/**
 * Health, identity, where it may go and who has it.
 *
 * The scope/assignment distinction is the one thing this record has to teach,
 * so it is still said in words under the card rather than assumed: where a
 * piece *may* be used is a standing decision, where it *is* comes from the
 * visits.
 */
function EquipmentRail({ record }: { record: EquipmentRecordData }) {
  const piece = record.equipment
  const combinedWeight = record.pairedWith.reduce(
    (total, partner) => total + (partner.curbWeightLb ?? 0), piece.curbWeightLb ?? 0)

  const fields: AboutField[] = [
    { label: 'Type', value: piece.typeName ?? EM_DASH },
    {
      label: 'Year, make',
      value: [piece.year, piece.make, piece.model].filter(Boolean).join(' ') || EM_DASH,
    },
    // The last four are enough to tell two trucks apart, and the rest is not
    // something a screen needs to shout.
    { label: 'VIN', value: piece.vin ? maskVin(piece.vin) : EM_DASH },
    {
      label: 'Plate',
      value: piece.plate
        ? `${piece.plate}${piece.plateExpiresOn ? ` · expires ${formatDocumentDate(piece.plateExpiresOn)}` : ''}`
        : EM_DASH,
    },
    { label: 'GVWR', value: piece.gvwrLb ? `${formatNumber(piece.gvwrLb)} lb` : EM_DASH },
    {
      label: 'Curb weight',
      value: piece.curbWeightLb ? `${formatNumber(piece.curbWeightLb)} lb` : EM_DASH,
    },
    {
      label: 'Decaled',
      value: piece.decaledOn ? formatDocumentDate(piece.decaledOn) : 'Not decaled',
    },
    { label: 'Branch', value: piece.orgUnitName ?? EM_DASH },
    {
      label: 'Mechanic',
      value: record.mechanic
        ? `${record.mechanic.name} · covers ${record.mechanic.orgUnitName}`
        : 'Nobody covers it',
    },
  ]

  return (
    <>
      <RecordCard title="Health">
        <div className="px-[18px] pb-4 pt-3">
          <div className="flex items-baseline gap-2">
            <span className={cn('tnum text-kpi',
              record.health.score >= 70 ? 'text-accent-dark'
                : record.health.score >= 40 ? 'text-warn' : 'text-danger')}>
              {record.health.score}
            </span>
            <span className="text-sub text-text-muted">out of 100</span>
          </div>
          <ProgressBar
            value={record.health.score}
            label="Health"
            tone={record.health.score >= 70 ? 'ok' : record.health.score >= 40 ? 'warn' : 'bad'}
          />
          {record.health.reasons.length === 0
            ? <p className="m-0 mt-2 text-sub text-text-muted">Nothing wrong with it.</p>
            : (
              <ul className="m-0 mt-2 list-none p-0">
                {record.health.reasons.map((reason) => (
                  <li key={reason} className="text-sub text-text-muted">· {reason}</li>
                ))}
              </ul>
            )}
        </div>
      </RecordCard>

      <AboutCard fields={fields} onEdit={record.canEdit ? record.onEdit : undefined} />

      {record.pairedWith.length > 0 && combinedWeight > 0 && (
        <RecordCard title="Paired with" count={record.pairedWith.length}>
          <div className="px-[18px] pb-4 pt-3">
            <p className="m-0 text-cell text-text">
              {record.pairedWith.map((partner) => partner.name).join(' and ')} — assigning one
              assigns both.
            </p>
            <p className={cn('m-0 mt-2 rounded-field px-2 py-1.5 text-sub',
              combinedWeight >= 26000
                ? 'bg-danger-bg text-danger'
                : combinedWeight >= 20000 ? 'bg-warn-bg text-text' : 'bg-surface-alt text-text-muted')}>
              Combined: {formatNumber(combinedWeight)} lb
              {combinedWeight >= 26000
                ? ' — over the CDL threshold.'
                : combinedWeight >= 20000 ? ' — worth a CDL-exempt check.' : '.'}
            </p>
          </div>
        </RecordCard>
      )}

      <RecordCard
        title="Where it can be used"
        footer="Any crew in one of these can be assigned it. Where it is today is an assignment,
                which comes from the visits."
      >
        <div className="px-[18px] pb-4 pt-3">
          {record.scope.length === 0
            ? (
              <p className="m-0 text-cell text-text">
                {piece.orgUnitName ?? 'Its own branch'} only.
              </p>
            )
            : (
              <div className="flex flex-wrap gap-1.5">
                {record.scope.map((unit) => <Tag key={unit.orgUnitId}>{unit.name}</Tag>)}
              </div>
            )}
        </div>
      </RecordCard>
    </>
  )
}

// ---------------------------------------------------------------------------
// The three own tabs
// ---------------------------------------------------------------------------

/**
 * What is due and what is wrong, on one tab.
 *
 * They were two places on the old page and they are one question: a service
 * line that has gone over raises a work order by itself (`origin =
 * 'service_due'`), so reading the schedule without the orders beside it means
 * reading half the answer.
 */
function ServiceTab({
  record, onChanged,
}: {
  record: EquipmentRecordData
  onChanged: (message: string) => void
}) {
  return (
    <>
      <ServiceCard
        service={record.service}
        templateName={record.equipment.serviceTemplateName}
      />

      <WorkOrders
        orders={record.workOrders}
        canEdit={record.canEdit}
        onRaise={record.onRaise}
        onChanged={onChanged}
      />

      <Card className="p-4">
        <SectionLabel className="mb-2 block">Meter readings</SectionLabel>
        {record.meterReadings.length === 0
          ? <p className="m-0 text-cell text-text-muted">Nobody has read it yet.</p>
          : record.meterReadings.map((reading, index) => (
            <div key={index}
              className="flex items-baseline justify-between border-b border-line-soft py-1.5 last:border-b-0">
              <span className="tnum text-cell text-text">
                {formatNumber(Number(reading.reading))}{' '}
                <span className="text-text-muted">{reading.kind}</span>
              </span>
              <span className="text-sub text-text-faint">
                {reading.source === 'quick_check' ? 'Morning check' : 'Office'} ·{' '}
                {formatRelative(reading.recordedAt)}
              </span>
            </div>
          ))}
      </Card>
    </>
  )
}

function Checks({ checks }: { checks: EquipmentRecordData['quickChecks'] }) {
  return (
    <Card className="overflow-hidden p-0">
      {checks.length === 0
        ? <p className="m-0 p-6 text-body text-text-muted">No checks yet.</p>
        : checks.map((check) => (
          <div key={check.id}
            className="flex items-baseline gap-2 border-b border-line-soft px-3 py-2.5 last:border-b-0">
            <span className="min-w-0 flex-1">
              <span className="block text-cell text-text">
                {check.byName ?? 'A crew'} ·{' '}
                {check.meterReading
                  ? `${formatNumber(Number(check.meterReading))}`
                  : 'no reading'}
              </span>
              <span className="block text-sub text-text-muted">
                {formatRelative(check.submittedAt)}
                {check.fuelLevel && ` · fuel ${check.fuelLevel.replace('_', ' ')}`}
              </span>
            </span>
            {check.issues > 0
              ? (
                <Tag tone="bad">
                  {check.issues} {check.issues === 1 ? 'issue' : 'issues'}
                </Tag>
              )
              : <span className="text-sub text-text-faint">No issues</span>}
          </div>
        ))}
    </Card>
  )
}

/** The last four characters, which is what tells two trucks apart. */
const maskVin = (vin: string) =>
  vin.length <= 4 ? vin : `${vin.slice(0, 8)}${'•'.repeat(Math.max(0, vin.length - 12))}${vin.slice(-4)}`

function ServiceCard({
  service, templateName,
}: {
  service: ServiceLine[]
  templateName: string | null
}) {
  return (
    <Card className="p-4">
      <div className="mb-2 flex flex-wrap items-baseline gap-2">
        <SectionLabel>Service schedule</SectionLabel>
        <span className="text-sub text-text-muted">{templateName ?? 'No schedule set'}</span>
      </div>

      {service.length === 0
        ? (
          <p className="m-0 text-cell text-text-muted">
            Nothing scheduled. A schedule belongs to a type, so every truck gets the same one.
          </p>
        )
        : service.map((line) => (
          <div key={line.lineId} className="border-b border-line-soft py-2 last:border-b-0">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-cell font-semibold text-text">{line.name}</span>
              <span className={cn('text-sub',
                line.overdue ? 'font-semibold text-danger'
                  : line.due ? 'text-warn' : 'text-text-muted')}>
                {describeDue(line)}
              </span>
            </div>
            <ProgressBar
              value={Math.min(100, Math.max(0, line.progress * 100))}
              label={`${line.name} progress`}
              tone={line.overdue ? 'bad' : line.due ? 'warn' : 'ok'}
            />
            <span className="text-sub text-text-faint">
              Every {formatNumber(line.interval)} {line.basis}
            </span>
          </div>
        ))}
    </Card>
  )
}

function describeDue(line: ServiceLine): string {
  if (line.basis === 'months') {
    if (line.overdue) {
      return `Overdue since ${line.dueOn ? formatDocumentDate(line.dueOn) : 'its date'}`
    }
    return `Due ${line.dueOn ? formatDateOnly(line.dueOn) : 'soon'}`
  }
  if (line.overdue) return `Overdue by ${formatNumber(Math.abs(line.remaining))} ${line.basis}`
  return `${formatNumber(line.remaining)} ${line.basis} to go`
}

function WorkOrders({
  orders, canEdit, onRaise, onChanged,
}: {
  orders: WorkOrder[]
  canEdit: boolean
  onRaise: () => void
  onChanged: (message: string) => void
}) {
  const [completing, setCompleting] = useState<WorkOrder | null>(null)

  return (
    <>
      <RecordCard
        title="Work orders"
        count={orders.length}
        action={canEdit ? { label: 'New', onClick: onRaise } : undefined}
      >
        <div className="px-[18px] py-2">
          {orders.length === 0
            ? <p className="m-0 py-2 text-body text-text-muted">Nothing wrong with it.</p>
            : orders.map((order) => (
              <div key={order.id} className="border-b border-line-soft py-2 last:border-b-0">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="text-cell font-semibold text-text">
                    {order.reference} · {order.title}
                  </span>
                  {order.closedAt
                    ? <Tag>Done</Tag>
                    : (
                      <Tag tone="bad">
                        {order.status === 'scheduled' ? 'Scheduled' : 'Open'}
                      </Tag>
                    )}
                  {order.takesOutOfService && !order.closedAt && <Tag>Off the road</Tag>}
                  {canEdit && !order.closedAt && (
                    <Button className="ml-auto" variant="ghost"
                      onClick={() => setCompleting(order)}>
                      Complete
                    </Button>
                  )}
                </div>
                <p className="m-0 text-sub text-text-muted">
                  {order.origin === 'quick_check' ? 'From a morning check'
                    : order.origin === 'service_due' ? 'Raised automatically — service overdue'
                      : 'Raised by hand'}
                  {order.assignedToName && ` · ${order.assignedToName}`}
                  {' · '}{formatRelative(order.openedAt)}
                </p>
                {order.detail && (
                  <p className="m-0 mt-1 text-sub text-text-muted">{order.detail}</p>
                )}
              </div>
            ))}
        </div>
      </RecordCard>

      {completing && (
        <CompleteWorkOrder
          order={completing}
          onClose={() => setCompleting(null)}
          onDone={(message) => { setCompleting(null); onChanged(message) }}
        />
      )}
    </>
  )
}

function RaiseWorkOrder({
  equipmentId, equipmentName, mechanic, onClose, onRaised,
}: {
  equipmentId: string
  equipmentName: string
  mechanic: { name: string; orgUnitName: string } | null
  onClose: () => void
  onRaised: (message: string) => void
}) {
  const [title, setTitle] = useState('')
  const [detail, setDetail] = useState('')
  const [offRoad, setOffRoad] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  return (
    <SlideOver
      open
      onClose={onClose}
      width={440}
      title={`New work order · ${equipmentName}`}
      footer={
        <div className="flex w-full items-center gap-2">
          {error && <span className="min-w-0 flex-1 text-sub text-danger">{error}</span>}
          <Button className="ml-auto" disabled={busy} onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={busy || !title.trim()}
            onClick={async () => {
              setBusy(true)
              try {
                const result = await request<{ reference: string }>('/work-orders', {
                  method: 'POST',
                  body: { equipmentId, title: title.trim(), detail, takesOutOfService: offRoad },
                })
                onRaised(`Raised ${result.reference}.`)
              } catch (cause) {
                setError(cause instanceof Error ? cause.message : 'That could not be raised.')
              } finally {
                setBusy(false)
              }
            }}>
            {busy ? 'Raising…' : 'Raise it'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="What is wrong with it?" required value={title} autoFocus
          placeholder="Check engine light" onChange={setTitle} />

        <label className="block">
          <span className="mb-1 block text-sub text-text-muted">Anything else</span>
          <textarea
            rows={3}
            value={detail}
            onChange={(event) => setDetail(event.currentTarget.value)}
            className="w-full rounded-field border border-line bg-surface p-2 text-cell text-text"
          />
        </label>

        <label className="flex items-start gap-2">
          <input
            type="checkbox"
            checked={offRoad}
            onChange={(event) => setOffRoad(event.currentTarget.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 accent-accent"
          />
          <span className="text-cell text-text">
            Take it off the road
            <span className="block text-sub text-text-faint">
              Dispatch stops offering it until the work order is closed.
            </span>
          </span>
        </label>

        <Card className="p-3">
          <p className="m-0 text-sub text-text-muted">
            {mechanic
              ? `This goes to ${mechanic.name}, who covers ${mechanic.orgUnitName}.`
              : 'Nobody covers this piece yet, so it waits in the unassigned queue.'}
          </p>
        </Card>
      </div>
    </SlideOver>
  )
}

function CompleteWorkOrder({
  order, onClose, onDone,
}: {
  order: WorkOrder
  onClose: () => void
  onDone: (message: string) => void
}) {
  const [hours, setHours] = useState('')
  const [parts, setParts] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  return (
    <Modal
      open
      onClose={onClose}
      title={`Complete ${order.reference}`}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" disabled={busy}
            onClick={async () => {
              setBusy(true)
              try {
                await request(`/work-orders/${order.id}/complete`, {
                  method: 'POST',
                  body: {
                    laborHours: Number(hours.replace(/[^0-9.]/g, '')) || 0,
                    partsCost: Number(parts.replace(/[^0-9.]/g, '')) || 0,
                    note: note.trim() || null,
                  },
                })
                onDone(`${order.reference} closed.`)
              } finally {
                setBusy(false)
              }
            }}>
            {busy ? 'Closing…' : 'Close it'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Hours" value={hours} onChange={setHours} />
          <Field label="Parts" value={parts} onChange={setParts} />
        </div>
        <label className="block">
          <span className="mb-1 block text-sub text-text-muted">What was done</span>
          <textarea
            rows={3}
            value={note}
            onChange={(event) => setNote(event.currentTarget.value)}
            className="w-full rounded-field border border-line bg-surface p-2 text-cell text-text"
          />
        </label>
        <p className="m-0 text-sub text-text-faint">
          A scheduled service closes its line at today’s meter, which is what the next
          interval counts from.
        </p>
      </div>
    </Modal>
  )
}

/** What owning it costs, and what it has earned back. */
function Costs({ detail }: { detail: Detail }) {
  const { equipment: piece, depreciation, usage } = detail
  const book = depreciation.book
  const tax = depreciation.tax

  return (
    <div className="grid gap-3 md:grid-cols-2">
      <Card className="p-4">
        <SectionLabel className="mb-2 block">Owning it</SectionLabel>
        <KVGrid rows={[
          { key: 'Purchased', value: piece.purchasedOn
            ? `${formatDocumentDate(piece.purchasedOn)} · ${formatRate(Number(piece.purchaseCost))}`
            : EM_DASH },
          { key: 'Tax method', value: methodLabel(piece.taxMethod) },
          { key: 'Book method', value: `${methodLabel(piece.bookMethod)} over ${piece.bookLifeMonths} months` },
          { key: 'Taken (book)', value: book ? formatRate(Number(book.taken)) : EM_DASH },
          { key: 'Book value', value: book?.bookValue ? formatRate(Number(book.bookValue)) : EM_DASH },
          { key: 'Taken (tax)', value: tax ? formatRate(Number(tax.taken)) : EM_DASH },
        ]} />
        <p className="m-0 mt-2 text-sub text-text-faint">
          The two methods disagree on purpose: the tax one is what the accountant files, the
          book one is what a job is costed against.
        </p>
      </Card>

      <Card className="p-4">
        <SectionLabel className="mb-2 block">Earning it back</SectionLabel>
        <KVGrid rows={[
          { key: 'Hours this month', value: `${formatNumber(Number(usage.hours))} h` },
          { key: 'Visits', value: formatNumber(usage.visits) },
          { key: 'Cost to jobs', value: formatRate(Number(usage.cost)) },
          { key: 'Cost per hour', value: piece.internalHourlyRate
            ? formatRate(Number(piece.internalHourlyRate))
            : 'Not costed to jobs' },
          /*
           * What a customer pays is the kind's (0129): every machine of the
           * type is charged the one price on the catalog's Equipment tab.
           */
          { key: 'Charged per unit', value: piece.kindPrice && Number(piece.kindPrice) > 0
            ? `${formatRate(Number(piece.kindPrice))} per ${piece.kindUnit ?? 'unit'} · as ${piece.kindName}`
            : piece.kindId ? `${piece.kindName} has no price yet` : 'Not on the catalog' },
        ]} />
        <p className="m-0 mt-2 text-sub text-text-faint">
          Hours come from visits. A piece with no hourly cost charges jobs nothing — the money
          in a trailer is in owning it.
        </p>
      </Card>
    </div>
  )
}

const methodLabel = (method: string): string => ({
  none: 'Not depreciated',
  macrs_5: 'MACRS 5-year',
  macrs_7: 'MACRS 7-year',
  straight_line: 'Straight line',
}[method] ?? method)
